// Mime classification used both by the upload router (video → recording,
// everything else → attachment) and by the FilesTab section split.

import type { IconName } from "@/components/Icon";

export type FileCategory = "documents" | "images" | "audio" | "other";

const DOC_EXT = new Set([
  "pdf",
  "doc",
  "docx",
  "rtf",
  "txt",
  "odt",
  "xls",
  "xlsx",
  "csv",
  "ods",
  "ppt",
  "pptx",
  "odp",
]);

const DOC_MIME_PREFIXES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument",
  "application/vnd.oasis.opendocument",
  "application/rtf",
  "text/plain",
  "text/rtf",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
];

export function classifyFile(mime: string | null | undefined, filename: string | null | undefined): FileCategory {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("image/")) return "images";
  if (m.startsWith("audio/")) return "audio";
  if (DOC_MIME_PREFIXES.some((p) => m.startsWith(p))) return "documents";
  const ext = (filename || "").split(".").pop()?.toLowerCase() || "";
  if (DOC_EXT.has(ext)) return "documents";
  return "other";
}

// Fine-grained icon for the file row inside the «Documents» / «Other» sections.
export function iconForFile(mime: string | null | undefined, filename: string | null | undefined): IconName {
  const cat = classifyFile(mime, filename);
  if (cat === "images") return "image";
  if (cat === "audio") return "music";
  const ext = (filename || "").split(".").pop()?.toLowerCase() || "";
  if (["zip", "rar", "7z", "tar", "gz", "bz2"].includes(ext)) return "archive";
  const m = (mime || "").toLowerCase();
  if (m.includes("zip") || m.includes("compressed") || m.includes("archive")) return "archive";
  return "file-text";
}

export const RUSSIAN_CATEGORY_LABEL: Record<FileCategory, string> = {
  documents: "Документы",
  images: "Изображения",
  audio: "Аудио",
  other: "Прочее",
};

// True when the file should be uploaded through the manual-recording pipeline
// (a Meeting + MeetingRecording row + STT), not the generic attachments route.
// Both video and audio go here — the backend transcribes both via one pipeline
// (see ats_api.transcription.build_transcript).
export function isMediaFile(mime: string | null | undefined, filename: string | null | undefined): boolean {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("video/") || m.startsWith("audio/")) return true;
  const ext = (filename || "").split(".").pop()?.toLowerCase() || "";
  return [
    // video
    "mp4", "mov", "webm", "mkv", "avi", "m4v",
    // audio
    "mp3", "wav", "m4a", "ogg", "oga", "opus", "flac", "aac", "wma", "amr",
  ].includes(ext);
}

export function isAudioFile(mime: string | null | undefined, filename: string | null | undefined): boolean {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("audio/")) return true;
  const ext = (filename || "").split(".").pop()?.toLowerCase() || "";
  return ["mp3", "wav", "m4a", "ogg", "oga", "opus", "flac", "aac", "wma", "amr"].includes(ext);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}
